import { Injectable } from '@angular/core';
import { Http, QueryBus, Weaver, WeaverBase } from '@skylab-angular-shared/core';
import { deserializeInventoryDocumentBuyerModel, deserializeInventoryDocumentClientModel, documentDeliveryAddressFilled, serializeAddressModel, serializeDocumentExchangeRateModel, serializeInventoryDocumentBuyerModel, serializeInventoryDocumentClientModel } from '@skylab-shared/business';
import { HttpService, ObservableCollection, Repository, RepositoryBase, TaggedUrl, Tags } from '@skylab-shared/core';
import { AddressModel, CalculationMethod, ExchangeRateModel, InventoryDocumentItemModel, InventoryGetGoodsIssueDocumentDTO, InventoryGoodsIssueAdjustmentDocumentModel, InventoryUpdateGoodsIssueDocumentDTO, RelatedEntity, VatTableItemModel } from '@skylab-shared/dataModel';
import { getStartOfDate, pickBy } from '@skylab-shared/utils';
import { ProductRepository } from '../product/product.repo';
import { SaleDocumentAdjustmentRepository, SaleDocumentAdjustmentRepositoryBase } from '../sale/sale-document-adjustment.repo';
import { InventoryGoodsIssueAdjustmentDocumentBO } from './inventory-goods-issue-adjustment-document.bo';

@Repository({
    boType: InventoryGoodsIssueAdjustmentDocumentBO,
    apiController: TaggedUrl.join('inventory/goodsIssue/adjustments', [Tags.Api]),
    dto: {
        get: InventoryGetGoodsIssueDocumentDTO,
        update: InventoryUpdateGoodsIssueDocumentDTO
    }
})
@Injectable({ providedIn: 'root' })
export class InventoryGoodsIssueAdjustmentDocumentRepository extends RepositoryBase<InventoryGoodsIssueAdjustmentDocumentBO, InventoryGoodsIssueAdjustmentDocumentModel, InventoryGetGoodsIssueDocumentDTO, InventoryUpdateGoodsIssueDocumentDTO> {

    public constructor(
        private readonly weaver: Weaver,
        private readonly queryBus: QueryBus,
        private readonly productRepository: ProductRepository,
        private readonly saleAdjustmentRepo: SaleDocumentAdjustmentRepository,
        http: Http
    ) {
        super(http as HttpService);
    }

    protected createBO(): InventoryGoodsIssueAdjustmentDocumentBO {
        return new InventoryGoodsIssueAdjustmentDocumentBO(this.weaver as WeaverBase, this.productRepository, this.queryBus as any, this.saleAdjustmentRepo as SaleDocumentAdjustmentRepositoryBase);
    }

    protected async preparePlainObjectAsync(model: InventoryGoodsIssueAdjustmentDocumentModel): Promise<InventoryUpdateGoodsIssueDocumentDTO> {
        return {
            duplicatePreventionId: model.duplicatePreventionId,
            id: model.id,
            version: model.version,
            currencyId: model.currencyId,
            calculationMethod: model.calculationMethod,
            communityTransactionId: model.communityTransactionId,
            issuer: model.issuer,
            adjustedDocumentVersion: model.adjustedDocumentVersion,
            adjustedDocumentId: model.adjustedDocumentId,
            note: model.note,
            relatedCommercialDocuments: model.relatedCommercialDocuments.map(doc => ({
                id: doc.id,
                version: doc.version
            })) || [],
            recipient: model.recipient,
            issueDate: getStartOfDate(model.issueDate),
            numberingId: model.numberingId,
            goodsIssueDate: getStartOfDate(model.goodsIssueDate),
            deliveryAddress: documentDeliveryAddressFilled(model.deliveryAddress) ? serializeAddressModel(model.deliveryAddress!) : null,
            grossValue: model.grossValue,
            netValue: model.netValue,
            transactionLocation: model.transactionLocation,
            wstoShipmentCountryId: model.wstoShipmentCountryId,
            wstoConsumerCountryId: model.wstoConsumerCountryId,
            wstoAdditionalInformation: model.wstoAdditionalInformation,
            documentNumber: model.documentNumber,
            exchangeRate: model.exchangeRate ? serializeDocumentExchangeRateModel(model.exchangeRate) : null,
            creationMethod: model.creationMethod,
            items: model.items.map(elm => ({
                id: elm.id,
                discountValue: elm.discountValue,
                discountKind: elm.discountKind,
                no: elm.ordinalNumber,
                note: elm.note,
                productId: elm.productId,
                productName: elm.productName,
                adjustedItemId: elm.adjustedItemId,
                pkwiu: elm.pkwiu,
                relatedCommercialItemsIds: elm.relatedCommercialItemsIds || [],
                cnCode: elm.cnCode,
                symbol: elm.symbol,
                barCode: elm.barCode,
                jpkVatProductGroup: elm.jpkVatGroup,
                quantity: elm.quantity,
                uniqueKey: elm.uniqueKey,
                unitId: elm.unitId,
                price: model.calculationMethod === CalculationMethod.Gross ? elm.price_grossBeforeDiscount : elm.price_netBeforeDiscount,
                vatRateId: elm.vatRateId
            })),
            inventoryId: model.inventoryId,
            kind: model.kind,
            client: serializeInventoryDocumentClientModel(model.client),
            buyer: serializeInventoryDocumentBuyerModel(model.buyer)
        };
    }

    protected async fromPlainObjectAsync(item: InventoryGetGoodsIssueDocumentDTO): Promise<InventoryGoodsIssueAdjustmentDocumentModel> {
        return new InventoryGoodsIssueAdjustmentDocumentModel(pickBy<InventoryGoodsIssueAdjustmentDocumentModel>({
            duplicatePreventionId: item.duplicatePreventionId,
            id: item.id,
            version: item.version,
            currencyId: item.currencyId,
            calculationMethod: item.calculationMethod,
            communityTransactionId: item.communityTransactionId,
            grossValue: item.grossValue,
            note: item.note,
            relatedCommercialDocuments: item.relatedCommercialDocuments.map(doc => new RelatedEntity(doc.id, doc.version)) || [],
            netValue: item.netValue,
            issuer: item.issuer,
            adjustedDocumentVersion: item.adjustedDocumentVersion,
            adjustedDocumentId: item.adjustedDocumentId,
            recipient: item.recipient,
            wstoShipmentCountryId: item.wstoShipmentCountryId,
            wstoConsumerCountryId: item.wstoConsumerCountryId,
            wstoAdditionalInformation: item.wstoAdditionalInformation,
            issueDate: item.issueDate,
            numberingId: item.numberingId,
            kind: item.kind,
            goodsIssueDate: item.goodsIssueDate,
            transactionLocation: item.transactionLocation,
            issuedByVatPayer: item.issuedByVatPayer,
            documentNumber: item.documentNumber,
            inventoryId: item.inventoryId,
            items: new ObservableCollection(item.items.map(documentItem => {
                return new InventoryDocumentItemModel(pickBy<Partial<InventoryDocumentItemModel>>({
                    ordinalNumber: documentItem.no,
                    discountKind: documentItem.discountKind,
                    discountValue: documentItem.discountValue,
                    note: documentItem.note,
                    price_grossAfterDiscount: documentItem.unitGrossPriceAfterDiscount,
                    price_grossBeforeDiscount: documentItem.unitGrossPrice,
                    price_netAfterDiscount: documentItem.unitNetPriceAfterDiscount,
                    price_netBeforeDiscount: documentItem.unitNetPrice,
                    relatedCommercialItemsIds: documentItem.relatedCommercialItemsIds || [],
                    id: documentItem.id,
                    productId: documentItem.productId,
                    productName: documentItem.productName,
                    pkwiu: documentItem.pkwiu,
                    cnCode: documentItem.cnCode,
                    jpkVatGroups: documentItem.jpkVatProductGroup ? [documentItem.jpkVatProductGroup] : [],
                    quantity: documentItem.quantity,
                    uniqueKey: documentItem.uniqueKey,
                    symbol: documentItem.symbol,
                    barCode: documentItem.barCode,
                    adjustedItemId: documentItem.adjustedItemId,
                    unitId: documentItem.unitId,
                    value_grossAfterDiscount: documentItem.grossValueAfterDiscount,
                    value_grossBeforeDiscount: documentItem.grossValue,
                    value_netAfterDiscount: documentItem.netValueAfterDiscount,
                    value_netBeforeDiscount: documentItem.netValue,
                    value_vatAfterDiscount: documentItem.vatValueAfterDiscount,
                    value_vatBeforeDiscount: documentItem.vatValue,
                    vatRateId: documentItem.vatRateId,
                    inventoryTotalCost: documentItem.inventoryTotalCost,
                    inventoryUnitCost: documentItem.inventoryUnitCost,
                }, val => val !== void 0));
            })),
            client: deserializeInventoryDocumentClientModel(item.client),
            buyer: deserializeInventoryDocumentBuyerModel(item.buyer),
            exchangeRate: item.exchangeRate ? new ExchangeRateModel(pickBy<ExchangeRateModel>({
                date: item.exchangeRate.date,
                manual: item.exchangeRate.manual,
                rate: item.exchangeRate.rate,
                tableNo: item.exchangeRate.tableNo,
                units: item.exchangeRate.units
            }, val => val !== void 0)) : null,
            vatTableItems: new ObservableCollection(item.vatTable.items.map(item => {
                return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                    grossValue: item.currencyGross ?? item.gross,
                    netValue: item.currencyNet ?? item.net,
                    vatRateId: item.vatRateId,
                    vatValue: item.currencyVat ?? item.vat,
                    cost: item.cost
                }, val => val !== void 0));
            })),
            vatTableItemsInBaseCurrency: item.exchangeRate ? new ObservableCollection(item.vatTable.items.map(item => {
                return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                    grossValue: item.gross,
                    netValue: item.net,
                    vatRateId: item.vatRateId,
                    vatValue: item.vat,
                    cost: item.cost
                }, val => val !== void 0));
            })) : new ObservableCollection(),
            deliveryAddress: item.deliveryAddress ? new AddressModel(pickBy<AddressModel>({
                line1: item.deliveryAddress.address,
                name: item.deliveryAddress.name,
                city: item.deliveryAddress.city,
                countryId: item.deliveryAddress.countryId,
                zipCode: item.deliveryAddress.zipCode
            }, val => val !== void 0)) : null
        }, val => val !== void 0));
    }
}
